// src/pages/ProdutosIntelbras.js
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import './ProdutosIntelbras.css';
import ContactForm from '../components/ContactForm';

const ProdutosIntelbras = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilters, setSelectedFilters] = useState({
        unidade: null,
        segmento: null,
        familia: null,
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage, setProductsPerPage] = useState(10);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const prodPath = "pts-com-br/fabricantes/intelbras_products";
                const prodQuery = query(collection(db, prodPath), orderBy("nome"));
                const prodSnapshot = await getDocs(prodQuery);
                const allProducts = prodSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setProducts(allProducts);
            } catch (err) {
                console.error("Erro ao buscar dados:", err);
            }
            setLoading(false);
        };
        fetchData();
    }, []);

    const filteredProducts = useMemo(() => {
        let tempProducts = products;

        if (searchTerm.trim() !== '') {
            const lowercasedTerm = searchTerm.toLowerCase();
            tempProducts = tempProducts.filter(p =>
                p.nome?.toLowerCase().includes(lowercasedTerm) ||
                p.resumo?.toLowerCase().includes(lowercasedTerm) ||
                p.sku?.toLowerCase().includes(lowercasedTerm) ||
                p.ean?.toLowerCase().includes(lowercasedTerm)
            );
        }

        if (selectedFilters.unidade) {
            tempProducts = tempProducts.filter(p => p.unidade === selectedFilters.unidade);
        }
        if (selectedFilters.segmento) {
            tempProducts = tempProducts.filter(p => p.segmento === selectedFilters.segmento);
        }
        if (selectedFilters.familia) {
            tempProducts = tempProducts.filter(p => p.familia === selectedFilters.familia);
        }

        return tempProducts;
    }, [products, searchTerm, selectedFilters]);

    const paginatedProducts = useMemo(() => {
        const indexOfLastProduct = currentPage * productsPerPage;
        const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
        return filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    }, [filteredProducts, currentPage, productsPerPage]);

    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    const handleFilterClick = (level, value) => {
        setCurrentPage(1);
        setSelectedFilters(prev => {
            if (level === 'unidade') {
                return { unidade: prev.unidade === value ? null : value, segmento: null, familia: null };
            }
            if (level === 'segmento') {
                return { ...prev, segmento: prev.segmento === value ? null : value, familia: null };
            }
            if (level === 'familia') {
                return { ...prev, familia: prev.familia === value ? null : value };
            }
            return prev;
        });
    };

    const unidades = useMemo(() => [...new Set(products.map(p => p.unidade).filter(Boolean))].sort(), [products]);
    const getSegmentosForUnidade = (unidade) => [...new Set(products.filter(p => p.unidade === unidade).map(p => p.segmento).filter(Boolean))].sort();
    const getFamiliasForSegmento = (segmento) => [...new Set(products.filter(p => p.segmento === segmento).map(p => p.familia).filter(Boolean))].sort();

    const formatPrice = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    return (
        <div className="product-page-intelbras">
            <div className="page-header">
                <h1>Catálogo de Soluções Intelbras</h1>
                <p>Navegue por nossas categorias e encontre o produto ideal para sua necessidade. Oferecemos a solução completa: do equipamento à instalação profissional.</p>
            </div>

            {/* /// INÍCIO DO NOVO BLOCO DE TEXTO /// */}
            <div className="solucao-completa-box">
                <h3>Não Compre Apenas Produtos. Invista em um Projeto Completo.</h3>
                <p>
                    Cuidamos de todo o seu projeto de segurança e automação Intelbras, do planejamento à instalação. Atendemos residências, condomínios e empresas com a mesma excelência. A solução certa, instalada por quem entende, é o que garante sua tranquilidade.
                </p>
                <p className="cta-text">
                    <strong>Cansado de listas de compras?</strong> Pule essa etapa. Vá até nosso formulário de contato, explique seu projeto e deixe o trabalho pesado conosco. Solicite seu orçamento completo.
                </p>
            </div>
            {/* /// FIM DO NOVO BLOCO DE TEXTO /// */}

            <div className="product-layout-intelbras">
                <aside className="product-sidebar-intelbras">
                    <h3>Categorias</h3>
                    <ul className="category-menu">
                        {unidades.map(unidade => {
                            const segmentos = getSegmentosForUnidade(unidade);
                            const isUnidadeOpen = selectedFilters.unidade === unidade;
                            return (
                                <li key={unidade}>
                                    <span onClick={() => handleFilterClick('unidade', unidade)} className={`category-level-1 ${isUnidadeOpen ? 'active' : ''}`}>
                                        {unidade}
                                        <span className="indicator">{isUnidadeOpen ? '-' : '+'}</span>
                                    </span>
                                    {isUnidadeOpen && (
                                        <ul>
                                            {segmentos.map(segmento => {
                                                const familias = getFamiliasForSegmento(segmento);
                                                const isSegmentoOpen = selectedFilters.segmento === segmento;
                                                return (
                                                <li key={segmento}>
                                                    <span onClick={() => handleFilterClick('segmento', segmento)} className={`category-level-2 ${isSegmentoOpen ? 'active' : ''}`}>
                                                        {segmento}
                                                        {familias.length > 0 && <span className="indicator">{isSegmentoOpen ? '-' : '+'}</span>}
                                                    </span>
                                                    {isSegmentoOpen && familias.length > 0 && (
                                                        <ul>
                                                            {familias.map(familia => (
                                                                <li key={familia}>
                                                                    <span onClick={() => handleFilterClick('familia', familia)} className={`category-level-3 ${selectedFilters.familia === familia ? 'active' : ''}`}>
                                                                        {familia}
                                                                    </span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            )})}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </aside>
                <main className="product-content-intelbras">
                    <div className="toolbar">
                        <input
                            type="text"
                            placeholder="Buscar por nome, SKU, EAN..."
                            className="search-input"
                            value={searchTerm}
                            onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                        <div className="pagination-controls">
                            <span>Exibir:</span>
                            <select value={productsPerPage} onChange={e => { setProductsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="30">30</option>
                                <option value="40">40</option>
                                <option value="50">50</option>
                            </select>
                        </div>
                    </div>

                    {loading ? (
                        <p>Carregando produtos...</p>
                    ) : (
                        <>
                            <div className="product-grid-intelbras">
                                {paginatedProducts.map(prod => (
                                    <div key={prod.id} className="product-card-intelbras">
                                        <img src={prod.mainImage} alt={prod.nome} />
                                        <h4>{prod.nome}</h4>
                                        <p className="sku">Cód: {prod.sku}</p>
                                        <div className="card-price">{formatPrice(prod.precoSugerido)}</div>
                                        <Link to={`/produtos/intelbras/${prod.slug}`} className="details-button">
                                            Ver Detalhes
                                        </Link>
                                    </div>
                                ))}
                            </div>
                            {filteredProducts.length === 0 && <p>Nenhum produto encontrado com os filtros aplicados.</p>}
                            
                            {totalPages > 1 && (
                                <div className="pagination-nav">
                                    <button onClick={() => setCurrentPage(c => Math.max(1, c - 1))} disabled={currentPage === 1}>
                                        Anterior
                                    </button>
                                    <span>Página {currentPage} de {totalPages}</span>
                                    <button onClick={() => setCurrentPage(c => Math.min(totalPages, c + 1))} disabled={currentPage === totalPages}>
                                        Próxima
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
            <ContactForm />
        </div>
    );
};

export default ProdutosIntelbras;